/**
 * Shared git worktree helper for service connectors.
 *
 * Provides create/cleanup with a cross-adapter ownership registry
 * that prevents one adapter from removing a worktree another is using.
 *
 * Every git invocation here is async and carries an explicit timeout. These
 * helpers are reachable from agent-invokable connector operations (the
 * static-server `serve`/`stop` ops), so a git call whose `cwd` sits on a wedged
 * mount used to park the runner's single event loop indefinitely.
 */

import { execFile } from "node:child_process";
import { mkdir } from "node:fs/promises";
import { resolve } from "node:path";
import { promisify } from "node:util";
import { withFsDeadline } from "@skaile/workspaces/core";

const execFileAsync = promisify(execFile);

// ── Types ───────────────────────────────────────────────────────────────────

/**
 * Result of a successful `createWorktree` call. The `cleanup` function removes the
 * worktree when no other owners are registered for the same path.
 * @docLink packages/connectors/api-reference#worktree-result
 */
export interface WorktreeResult {
  /** Absolute path to the worktree directory. */
  path: string;
  /** Resolved full SHA of the ref. */
  ref: string;
  /** Branch name if ref was a branch. */
  branch?: string;
  /** Remove the worktree. Fails if other owners exist in registry. */
  cleanup: () => Promise<void>;
}

/**
 * Summary entry returned by `listWorktrees`.
 * @docLink packages/connectors/api-reference#worktree-info
 */
export interface WorktreeInfo {
  /** Absolute path to the worktree. */
  path: string;
  /** HEAD commit SHA. */
  head: string;
  /** Branch reference, if a named branch is checked out. */
  branch?: string;
}

// ── Registry ────────────────────────────────────────────────────────────────

class WorktreeRegistryImpl {
  private readonly entries = new Map<string, Set<string>>();

  register(path: string, owner: string): void {
    let owners = this.entries.get(path);
    if (!owners) {
      owners = new Set();
      this.entries.set(path, owners);
    }
    owners.add(owner);
  }

  deregister(path: string, owner: string): void {
    const owners = this.entries.get(path);
    if (!owners) return;
    owners.delete(owner);
    if (owners.size === 0) this.entries.delete(path);
  }

  isInUse(path: string): boolean {
    return (this.entries.get(path)?.size ?? 0) > 0;
  }

  owners(path: string): string[] {
    return [...(this.entries.get(path) ?? [])];
  }
}

/**
 * Module-level singleton registry shared across all service-connector adapters in the same process.
 * Prevents one adapter from removing a worktree that another adapter is still using.
 * @docLink packages/connectors/api-reference#worktree-registry
 */
export const worktreeRegistry = new WorktreeRegistryImpl();

// ── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Deadline for metadata-only git queries (`rev-parse`, `worktree list`). These
 * read `.git` and return immediately on a healthy repository; anything slower
 * means the filesystem under `cwd` is not answering.
 */
export const GIT_QUERY_TIMEOUT_MS = 15_000;

/**
 * Deadline for git operations that touch the working tree (`worktree add`,
 * `worktree remove`, `worktree prune`). Checking out a large tree is legitimate
 * work, so the budget is wider than a query's.
 */
export const GIT_WORKTREE_TIMEOUT_MS = 120_000;

/**
 * Run `git` and return trimmed stdout.
 *
 * `timeoutMs` has no default **by design**: the previous `execSync` form
 * carried no timeout at all, and every call site inherited that. Requiring the
 * bound makes an unbounded git call unrepresentable.
 *
 * Killed with `SIGKILL`, not `SIGTERM` — a process blocked in an
 * uninterruptible kernel wait (`D` state, e.g. a git call whose `cwd` sits on a
 * dead FUSE connection) never runs a `SIGTERM` handler, so a graceful signal
 * would leave the deadline unenforced. Note the residual case this cannot fix:
 * a truly `D`-state child ignores `SIGKILL` too, which is exactly why the call
 * is also async — the event loop stays free either way.
 *
 * @param args - Argv after `git`. Array form, so no shell quoting is involved.
 * @param cwd - Working directory for the git invocation.
 * @param timeoutMs - Wall-clock limit for the child.
 */
async function git(args: string[], cwd: string, timeoutMs: number): Promise<string> {
  const { stdout } = await execFileAsync("git", args, {
    cwd,
    encoding: "utf-8",
    timeout: timeoutMs,
    killSignal: "SIGKILL",
  });
  return stdout.trim();
}

async function resolveRef(repoRoot: string, ref: string): Promise<string> {
  return git(["rev-parse", "--verify", ref], repoRoot, GIT_QUERY_TIMEOUT_MS);
}

/**
 * Walk up from `fromDir` to find the git repository root.
 * @param fromDir - Any directory inside a git repository.
 * @returns Absolute path to the repository root.
 * @throws {Error} when no git repository is found, or when git does not answer
 *   within {@link GIT_QUERY_TIMEOUT_MS}.
 * @docLink packages/connectors/api-reference#find-git-root
 */
export async function findGitRoot(fromDir: string): Promise<string> {
  return git(["rev-parse", "--show-toplevel"], fromDir, GIT_QUERY_TIMEOUT_MS);
}

// ── Public API ──────────────────────────────────────────────────────────────

/**
 * Create a git worktree for `ref` under `baseDir` and register it with `worktreeRegistry`.
 * The returned `WorktreeResult.cleanup()` removes the worktree when no other owners remain.
 * @param opts - Repo root, git ref, base directory, optional label, and owner ID.
 * @returns Path, resolved SHA, branch name, and `cleanup()` function.
 * @throws {Error} if the ref cannot be resolved.
 * @docLink packages/connectors/api-reference#create-worktree
 */
export async function createWorktree(opts: {
  repoRoot: string;
  ref: string;
  baseDir: string;
  label?: string;
  owner: string;
}): Promise<WorktreeResult> {
  const sha = await resolveRef(opts.repoRoot, opts.ref);
  const label = opts.label ?? sha.slice(0, 8);
  const wtPath = resolve(opts.baseDir, label);

  // `recursive: true` is already a no-op on an existing dir, so the former
  // existsSync probe only bought a second filesystem round-trip.
  await withFsDeadline(`mkdir ${opts.baseDir}`, () => mkdir(opts.baseDir, { recursive: true }));

  await git(["worktree", "add", wtPath, sha, "--detach"], opts.repoRoot, GIT_WORKTREE_TIMEOUT_MS);
  worktreeRegistry.register(wtPath, opts.owner);

  let branch: string | undefined;
  try {
    const refName = await git(
      ["rev-parse", "--abbrev-ref", opts.ref],
      opts.repoRoot,
      GIT_QUERY_TIMEOUT_MS,
    );
    if (refName !== "HEAD" && refName !== sha) branch = refName;
  } catch {
    // Not a branch
  }

  return {
    path: wtPath,
    ref: sha,
    branch,
    cleanup: async () => {
      worktreeRegistry.deregister(wtPath, opts.owner);

      if (worktreeRegistry.isInUse(wtPath)) {
        const remaining = worktreeRegistry.owners(wtPath);
        throw new Error(`worktree in use by: ${remaining.join(", ")}`);
      }

      try {
        await git(
          ["worktree", "remove", wtPath, "--force"],
          opts.repoRoot,
          GIT_WORKTREE_TIMEOUT_MS,
        );
      } catch {
        // May already be gone
      }
      await git(["worktree", "prune"], opts.repoRoot, GIT_WORKTREE_TIMEOUT_MS);
    },
  };
}

/**
 * List all git worktrees for a repository.
 * @param repoRoot - Absolute path to the git repository root.
 * @returns Array of `WorktreeInfo` objects parsed from `git worktree list --porcelain`.
 * @docLink packages/connectors/api-reference#list-worktrees
 */
export async function listWorktrees(repoRoot: string): Promise<WorktreeInfo[]> {
  const raw = await git(["worktree", "list", "--porcelain"], repoRoot, GIT_QUERY_TIMEOUT_MS);
  const trees: WorktreeInfo[] = [];
  let current: Partial<WorktreeInfo> = {};

  for (const line of raw.split("\n")) {
    if (line.startsWith("worktree ")) {
      if (current.path) trees.push(current as WorktreeInfo);
      current = { path: line.slice("worktree ".length) };
    } else if (line.startsWith("HEAD ")) {
      current.head = line.slice("HEAD ".length);
    } else if (line.startsWith("branch ")) {
      current.branch = line.slice("branch ".length);
    }
  }
  if (current.path) trees.push(current as WorktreeInfo);

  return trees;
}
